import { isEmpty, isArray, isNull } from 'lodash';
import {
  takeEvery,
  takeLatest,
  takeLeading,
  put,
  select,
  delay,
  all,
  take,
} from 'redux-saga/effects';
import { channel } from 'redux-saga';
import { safeApply, safeCall } from './errors';
import {
  FETCH_CHAIN,
  FETCH_CHAIN_SUCCESS,
  FETCH_CHAINS,
  FETCH_CHAINS_SUCCESS,
  CREATE_CHAIN,
  CREATE_CHAIN_SUCCESS,
  CREATE_CHAIN_FAILURE,
  UPDATE_ACTIVE_CHAIN,
  UPDATE_ACTIVE_CHAIN_SUCCESS,
  SEND_MESSAGE,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_FAILURE,
  FORWARD_MESSAGE,
  FORWARD_MESSAGE_SUCCESS,
  FORWARD_MESSAGE_FAILURE,
  FORWARD_CHAIN,
  FORWARD_CHAIN_SUCCESS,
  FORWARD_CHAIN_FAILURE,
  LOAD_MESSAGES_PAGE,
  LOAD_MESSAGES_PAGE_SUCCESS,
  LOAD_MESSAGES_PAGE_FAILURE,
  SET_MAIL_TEMPLATES_GROUPS_SUCCESS,
  SET_MAIL_TEMPLATES,
  SET_MAIL_TEMPLATES_SUCCESS,
  SET_MAIL_TEMPLATE,
  SET_MAIL_TEMPLATE_SUCCESS,
  APPLY_MAIL_TEMPLATE,
  SEARCH_MAIL_TEMPLATES,
  SEARCH_MAIL_TEMPLATES_SUCCESS,
  SEARCH_MAIL_TEMPLATES_FAILURE,
  SELECT_FOUND_MAIL_TEMPLATE,
  SELECT_FOUND_MAIL_TEMPLATE_SUCCESS,
  CREATE_CHAIN_REQUEST,
  LOAD_HISTORY_MESSAGES_IN_CHAIN,
  LOAD_HISTORY_MESSAGES_IN_CHAIN_SUCCESS,
  LOAD_HISTORY_MESSAGES_PAGE_SUCCESS,
  SET_CHAIN_COPILOT_SUGGESTIONS,
} from '../actions/chains';
import {
  SELECT_TICKET,
  SET_TICKET,
  CREATE_TICKET_SUCCESS,
} from '../actions/tickets';
import fetchData from '../../api/fetchData';

import {
  serializeChain,
  serializeChains,
  serializeCopilotSuggestions,
} from '../serializers/chain';
import { serializeMessages } from '../serializers/message';

import {
  serializeMailTemplateGroups,
  serializeMailTemplates,
  serializeMailTemplatesSearch,
  serializeMailTemplate,
} from '../serializers/mailTemplate';
import { ROUTE } from '../../consts/pages';
import {
  fetchMailTemplatesGroups,
  fetchMailTemplates,
  fetchMailTemplatesSearch,
  fetchMailTemplate,
} from './mailTemplate';
import ActiveChainEmailChannelHandler from './socketHandlers/ActiveChainEmailChannelHandler';
import ChainListChannelHandler from './socketHandlers/ChainListChannelHandler';
import {
  SOCKET_EMAIL_CHANNEL,
  SOCKET_MESSENGER_CHANNEL,
  SOCKET_CHAIN_LIST_CHANNEL,
  SOCKET_CHAIN_CHANNEL,
} from '../../consts/socketChannels';
import {
  CHAIN_SERVICES,
  TYPES as CHAIN_TYPES,
  TEMPLATES_SEARCH,
} from '../../consts/chains';
import WebSocketConnector from '../../api/sockets/WebSocketConnector';
import { normalizeHTMLString, setAttachmentsInFormData } from './utils/utils';

const apiChainsUrl = `${process.env.API_HOST_URL}${process.env.API_CHAINS}`;
const apiMessagesUrl = `${process.env.API_HOST_URL}${process.env.API_MESSAGES}`;
const apiLogTemplateUrl = `${process.env.API_HOST_URL}${process.env.API_TEMPLATE_EVENTS}`;
const CHAIN_MESSAGE_PAGE_SIZE = 20;

const MANAGER_NOT_FOUND_ERROR = 'Не выбран ответственный в активном тикете.';
const ACTIVE_CHAIN_NOT_FOUND_ERROR = 'Не выбрана цепочка в активном тикете.';
const ACTIVE_TICKET_NOT_FOUND_ERROR = 'Не выбран активный тикет.';
const DATA_FETCH_FAILED_ERROR = 'Запрос на отправку не выполнился.';

export function* fetchChain({ payload: { id, history } }) {
  const data = yield safeCall(
    fetchData,
    `${apiChainsUrl}/${id}?size=${CHAIN_MESSAGE_PAGE_SIZE}`
  );
  const serializedChain = serializeChain(data);
  const { activeTicketId } = yield select(state => state.tickets);

  if (history && activeTicketId) {
    history.push(`${ROUTE.TICKETS}/${activeTicketId}/${id}`);
  }

  yield put({ payload: { chain: serializedChain }, type: FETCH_CHAIN_SUCCESS });
}

export function* fetchCopilotSuggestions({ payload: { chain } }) {
  if (!chain) return;

  const data = yield safeCall(
    fetchData,
    `${`${process.env.API_HOST_URL}${process.env.API_COPILOT_SUGGESTIONS}`}?chain_id=${
      chain.id
    }`
  );
  const suggestions = serializeCopilotSuggestions(data?.suggestions);

  yield put({ payload: { suggestions }, type: SET_CHAIN_COPILOT_SUGGESTIONS });
}

export function* sendMessage({
  payload: {
    body,
    from,
    imageUrl = null,
    attachments = [],
    subjects = null,
    subjectsCopy = [],
    subjectsHideCopy = [],
  },
}) {
  const activeChain = yield select(({ chains }) => chains.activeChain);
  const activeTicket = yield select(({ tickets }) => tickets.activeTicket);

  if (!activeTicket) {
    yield put({
      error: ACTIVE_TICKET_NOT_FOUND_ERROR,
      type: SEND_MESSAGE_FAILURE,
    });
  }

  const { manager } = activeTicket;

  if (!activeChain || !activeChain.id) {
    yield put({
      error: ACTIVE_CHAIN_NOT_FOUND_ERROR,
      type: SEND_MESSAGE_FAILURE,
    });
  }

  if (!manager || !manager.id) {
    yield put({ error: MANAGER_NOT_FOUND_ERROR, type: SEND_MESSAGE_FAILURE });
  }

  const formData = new FormData();

  formData.append('messenger', activeChain.service);

  formData.append('accepted_suggestion_id', activeChain.acceptedSuggestionId);

  if (activeChain.service === CHAIN_SERVICES.EMAIL) {
    formData.append('chain_id', activeChain.id);
    formData.append('content', normalizeHTMLString(body));
    formData.append('manager_id', manager.id);
    formData.append('client_id', activeTicket.client.id);

    if (typeof from === 'object' && from.value) {
      formData.append('from', from.value);
    }

    if (subjects) {
      formData.append('to', subjects.value);
    }

    if (!isEmpty(subjectsCopy) && isArray(subjectsCopy)) {
      const data = subjectsCopy.map(({ value }) => value).join(',');
      formData.append('cc', data);
    }

    if (!isEmpty(subjectsHideCopy) && isArray(subjectsHideCopy)) {
      const data = subjectsHideCopy.map(({ value }) => value).join(',');
      formData.append('bcc', data);
    }

    if (attachments.length) {
      setAttachmentsInFormData(formData, attachments);
    }
  } else {
    formData.append('chain_id', activeChain.id);
    formData.append('ticket_id', activeTicket.id);
    formData.append('content', body);

    if (attachments.length) {
      setAttachmentsInFormData(formData, attachments);
    }

    if (imageUrl) {
      formData.append('image_url', imageUrl);
    }
  }

  const res = yield safeCall(fetchData, apiMessagesUrl, {
    method: 'POST',
    body: formData,
  });

  if (!res || !res.messages) {
    yield put({
      error: DATA_FETCH_FAILED_ERROR,
      type: SEND_MESSAGE_FAILURE,
    });
    return;
  }

  const { messages, persons, attachments: attachmentsData } = res;

  const serializedMessages = serializeMessages(
    messages,
    persons,
    attachmentsData
  );

  yield put({
    payload: { messagesData: serializedMessages },
    type: SEND_MESSAGE_SUCCESS,
  });
}

export function* forwardMessage({
  payload: {
    messageId,
    subject,
    comment,
    body,
    from,
    imageUrl = null,
    attachments = [],
    forwardAttachments = [],
    subjects = null,
    subjectsCopy = [],
    subjectsHideCopy = [],
  },
}) {
  const activeChain = yield select(({ chains }) => chains.activeChain);
  const activeTicket = yield select(({ tickets }) => tickets.activeTicket);

  if (!activeTicket) {
    yield put({
      error: 'Ошибка! Нет активного тикета.',
      type: FORWARD_MESSAGE_FAILURE,
    });
  }

  const { manager } = activeTicket;

  if (!activeChain || !activeChain.id) {
    yield put({
      error: 'Ошибка! Нет активной переписки.',
      type: FORWARD_MESSAGE_FAILURE,
    });
  }

  if (!manager || !manager.id) {
    yield put({
      error: 'Ошибка! Менеджер не найден.',
      type: FORWARD_MESSAGE_FAILURE,
    });
  }

  const formData = new FormData();

  formData.append('email_id', messageId);
  formData.append('subject', subject);

  formData.append('messenger', 'email');

  formData.append('comment', comment);
  formData.append('content', body);

  if (typeof from === 'object' && from.value) {
    formData.append('from', from.value);
  }

  if (subjects) {
    formData.append('to', subjects.value);
  }

  if (!isEmpty(subjectsCopy) && isArray(subjectsCopy)) {
    const data = subjectsCopy.map(({ value }) => value).join(',');
    formData.append('cc', data);
  }

  if (!isEmpty(subjectsHideCopy) && isArray(subjectsHideCopy)) {
    const data = subjectsHideCopy.map(({ value }) => value).join(',');
    formData.append('bcc', data);
  }

  if (!isEmpty(forwardAttachments) && isArray(forwardAttachments)) {
    forwardAttachments.forEach(id => {
      formData.append('forward_attachments[]', id);
    });
  }

  if (attachments.length) {
    setAttachmentsInFormData(formData, attachments);
  }

  if (imageUrl) {
    formData.append('image_url', imageUrl);
  }

  const { error } = yield safeCall(
    fetchData,
    `${apiMessagesUrl}/forward_email`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (error) {
    yield put({
      error: 'Ошибка! Запрос на пересылку сообщения не выполнился.',
      type: FORWARD_MESSAGE_FAILURE,
    });
  } else {
    yield put({ type: FORWARD_MESSAGE_SUCCESS });
  }
}

export function* forwardChain({
  payload: {
    subject,
    comment,
    chainId,
    to,
    from,
    subjectsCopy = [],
    subjectsHideCopy = [],
  },
}) {
  const activeChain = yield select(({ chains }) => chains.activeChain);
  const activeTicket = yield select(({ tickets }) => tickets.activeTicket);

  if (!activeTicket) {
    yield put({
      error: 'Ошибка! Нет активного тикета.',
      type: FORWARD_CHAIN_FAILURE,
    });
  }

  const { manager } = activeTicket;

  if (!activeChain || !activeChain.id) {
    yield put({
      error: 'Ошибка! Нет активной переписки.',
      type: FORWARD_CHAIN_FAILURE,
    });
  }

  if (!manager || !manager.id) {
    yield put({
      error: 'Ошибка! Менеджер не найден.',
      type: FORWARD_CHAIN_FAILURE,
    });
  }

  const formData = new FormData();

  formData.append('subject', subject);
  formData.append('chain_id', chainId);
  formData.append('to', to);
  formData.append('from', from.value);
  formData.append('comment', comment);

  if (!isEmpty(subjectsCopy) && isArray(subjectsCopy)) {
    const data = subjectsCopy.map(({ value }) => value).join(',');
    formData.append('cc', data);
  }

  if (!isEmpty(subjectsHideCopy) && isArray(subjectsHideCopy)) {
    const data = subjectsHideCopy.map(({ value }) => value).join(',');
    formData.append('bcc', data);
  }

  const { error } = yield safeCall(
    fetchData,
    `${apiMessagesUrl}/forward_chain`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (error) {
    yield put({
      error: 'Ошибка! Запрос на пересылку цепочки не выполнился.',
      type: FORWARD_CHAIN_FAILURE,
    });
  } else {
    yield put({ type: FORWARD_CHAIN_SUCCESS });
  }
}

export function* createChain({
  payload: {
    subject,
    body,
    from,
    attachments = [],
    subjects,
    subjectsCopy,
    subjectsHideCopy,
    history,
  },
}) {
  const { tickets, general } = yield select();
  const newChainType = yield select(state => state.chains.newChainType);
  const { activeTicket, activeTicketId } = tickets;
  const { currentManager } = general;

  const formData = new FormData();
  const { manager: ticketManager } = activeTicket;

  formData.append('content', body);
  formData.append('ticket_id', activeTicketId);
  formData.append('messenger', newChainType);
  formData.append('subject', subject);
  formData.append('manager_id', ticketManager?.id || currentManager?.id);
  formData.append('client_id', activeTicket.client.id);

  if (attachments.length) {
    setAttachmentsInFormData(formData, attachments);
  }

  if (newChainType === CHAIN_SERVICES.EMAIL) {
    if (subjects && subjects.value) {
      formData.append('to', subjects.value);
    }

    if (typeof from === 'object' && from.value) {
      formData.append('from', from.value);
    }

    const appendRecipients = (key, recipients) => {
      if (!isArray(recipients) || isEmpty(recipients)) {
        return;
      }

      const data = recipients
        .map(({ value }) => value)
        .filter(Boolean)
        .join(',');

      if (data) {
        formData.append(key, data);
      }
    };

    appendRecipients('cc', subjectsCopy);
    appendRecipients('bcc', subjectsHideCopy);
  }

  const res = yield safeCall(fetchData, apiMessagesUrl, {
    method: 'POST',
    body: formData,
  });

  if (!res || !res.chain) {
    yield put({
      error: 'Ошибка! Запрос на создание цепочки не выполнился.',
      type: CREATE_CHAIN_FAILURE,
    });
    return;
  }

  const { chain } = res;

  yield safeCall(fetchChain, {
    payload: {
      id: chain.id,
    },
  });

  if (history) {
    history.push(`${ROUTE.TICKETS}/${activeTicketId}/${chain.id}`);
  }

  const serializedChain = serializeChains([chain]);
  yield put({
    type: CREATE_CHAIN_SUCCESS,
    payload: { chain: serializedChain[0] },
  });
}

export function* updateActiveChain({
  payload: { from, subject, subjectsCopy, subjectsHideCopy },
}) {
  yield delay(1000);
  const activeChain = yield select(({ chains }) => chains.activeChain);
  const body = {};

  if (!activeChain) {
    // ActiveChain is not found
    return;
  }

  if (from) {
    body.from = from;
  }

  if (subject) {
    body.subject = subject;
  }

  if (isArray(subjectsCopy)) {
    body.cc = subjectsCopy.map(({ value }) => value).join(',');
  } else if (isNull(subjectsCopy)) {
    body.cc = '';
  }

  if (isArray(subjectsHideCopy)) {
    body.bcc = subjectsHideCopy.map(({ value }) => value).join(',');
  } else if (isNull(subjectsHideCopy)) {
    body.bcc = '';
  }

  const data = yield safeCall(fetchData, `${apiChainsUrl}/${activeChain.id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });

  const chain = serializeChain(data);

  yield put({ payload: { chain }, type: UPDATE_ACTIVE_CHAIN_SUCCESS });
}

export function* fetchChains() {
  yield put({ type: FETCH_CHAINS });
  const ticketId = yield select(({ tickets }) => tickets.activeTicketId);
  const { chains } = yield safeCall(
    fetchData,
    `${apiChainsUrl}/ticket/${ticketId}`
  );
  const serializedChains = serializeChains(chains);

  yield put({
    payload: { chains: serializedChains },
    type: FETCH_CHAINS_SUCCESS,
  });
}

export function* fetchMessagesPage({
  payload: { isNextPage = true, isRewriteMessages = false, isLastPage = false },
}) {
  const activeChain = yield select(({ chains }) => chains.activeChain);
  const historyChain = yield select(({ chains }) => chains.historyChain);
  let chain;

  if (historyChain) {
    chain = historyChain;
  } else {
    chain = activeChain;
  }

  const { id, totalPages, page } = chain;

  if (!id || !totalPages) {
    yield put({
      error: 'activeChain not found',
      type: LOAD_MESSAGES_PAGE_FAILURE,
    });
  }

  let nextPageIndex;
  if (isLastPage) {
    nextPageIndex = totalPages;
  } else {
    nextPageIndex = isNextPage
      ? Math.min(page + 1, totalPages)
      : Math.max(page - 1, 1);
  }

  if (isNextPage === page) return;
  const data = yield safeCall(
    fetchData,
    `${apiChainsUrl}/${id}?size=${CHAIN_MESSAGE_PAGE_SIZE}&page=${nextPageIndex}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const serializedChain = serializeChain(data);

  if (historyChain) {
    yield put({
      type: LOAD_HISTORY_MESSAGES_PAGE_SUCCESS,
      payload: {
        chain: serializedChain,
      },
    });
  } else {
    yield put({
      type: LOAD_MESSAGES_PAGE_SUCCESS,
      payload: {
        chain: serializedChain,
        isRewriteMessages,
      },
    });
  }
}

export function* setMailTemplatesGroupsInChain() {
  const mailTemplatesGroups = yield safeCall(fetchMailTemplatesGroups);
  const serializedMailTemplateGroups =
    serializeMailTemplateGroups(mailTemplatesGroups);
  yield put({
    type: SET_MAIL_TEMPLATES_GROUPS_SUCCESS,
    payload: {
      mailTemplateGroups: serializedMailTemplateGroups,
    },
  });
}

export function* setMailTemplatesInChain({ payload: { groupId } }) {
  const mailTemplates = yield safeCall(fetchMailTemplates, {
    payload: { groupId },
  });
  const serializedMailTemplates = serializeMailTemplates(mailTemplates);
  yield put({
    type: SET_MAIL_TEMPLATES_SUCCESS,
    payload: {
      mailTemplates: serializedMailTemplates,
    },
  });
}

export function* logApplyMailTemplate({ payload: { id: templateId } }) {
  const { id: currentManagerId } = yield select(
    state => state.general.currentManager
  );

  yield safeCall(fetchData, apiLogTemplateUrl, {
    method: 'POST',
    body: JSON.stringify({
      manager_id: currentManagerId,
      event_id: templateId,
    }),
    headers: { 'Content-Type': 'application/json' },
  });
}

export function* setMailTemplateInChain({ payload: { templateId } }) {
  const mailTemplate = yield safeCall(fetchMailTemplate, {
    payload: { templateId },
  });
  const serializedMailTemplate = serializeMailTemplate(mailTemplate);
  yield put({
    type: SET_MAIL_TEMPLATE_SUCCESS,
    payload: {
      mailTemplate: serializedMailTemplate,
    },
  });
}

// takeLatest отменяет предыдущий поиск, а delay даёт дописать запрос
export function* searchMailTemplatesInChain({ payload: { query } }) {
  if (query.trim().length < TEMPLATES_SEARCH.MIN_QUERY_LENGTH) return;

  yield delay(TEMPLATES_SEARCH.DEBOUNCE_MS);

  const response = yield safeCall(fetchMailTemplatesSearch, { query });

  if (!response?.meta?.success) {
    yield put({ type: SEARCH_MAIL_TEMPLATES_FAILURE, payload: { query } });
    return;
  }

  yield put({
    type: SEARCH_MAIL_TEMPLATES_SUCCESS,
    payload: { query, ...serializeMailTemplatesSearch(response, query) },
  });
}

export function* selectFoundMailTemplateInChain({ payload: { templateId } }) {
  const mailTemplate = yield safeCall(fetchMailTemplate, {
    payload: { templateId },
  });

  if (!mailTemplate?.mail_template) return;

  yield put({
    type: SELECT_FOUND_MAIL_TEMPLATE_SUCCESS,
    payload: { mailTemplate: serializeMailTemplate(mailTemplate) },
  });
}

function* watchSocketActiveChainEmailMessage(webSocketConnector, identifier) {
  const socketChannel = channel();
  const activeTicketChannelHandler = new ActiveChainEmailChannelHandler(
    socketChannel
  );
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: activeTicketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* unsubscribeSocketActiveChain() {
  const prevActiveChain = yield select(({ chains }) => chains.prevActiveChain);
  if (!prevActiveChain || !prevActiveChain.id) {
    return;
  }

  const { id, type } = prevActiveChain;
  const channel =
    type === CHAIN_TYPES.MAIL ? SOCKET_EMAIL_CHANNEL : SOCKET_MESSENGER_CHANNEL;

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.unsubscribeChannel({
    channel,
    chain_id: id,
  });
}

function* subscribeSocketActiveChain({ payload: { chain } }) {
  if (!chain) return;

  const { id, type } = chain;
  const channel =
    type === CHAIN_TYPES.MAIL ? SOCKET_EMAIL_CHANNEL : SOCKET_MESSENGER_CHANNEL;

  const identifier = {
    channel,
    chain_id: id,
  };

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(
    watchSocketActiveChainEmailMessage,
    webSocketConnector,
    identifier
  );
}

function* loadHistoryMessages({ payload: { chainId } }) {
  const data = yield safeCall(
    fetchData,
    `${apiChainsUrl}/${chainId}?size=${CHAIN_MESSAGE_PAGE_SIZE}`
  );
  const serializedChain = serializeChain(data);

  yield put({
    payload: { chain: serializedChain },
    type: LOAD_HISTORY_MESSAGES_IN_CHAIN_SUCCESS,
  });
}

function* watchSocketChainListMessage(webSocketConnector, identifier) {
  const socketChannel = channel();
  const activeTicketChannelHandler = new ChainListChannelHandler(socketChannel);
  webSocketConnector.subscribeSocketOnMessage({
    identifier,
    socketHandler: activeTicketChannelHandler,
  });

  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* unsubscribeSocketChainList() {
  const { prevActiveTicketId } = yield select(state => state.tickets);

  if (!prevActiveTicketId) {
    return;
  }

  const webSocketConnector = yield safeCall(WebSocketConnector.build);
  webSocketConnector.unsubscribeChannel({
    channel: SOCKET_CHAIN_CHANNEL,
    ticket_id: prevActiveTicketId,
  });
}

function* subscribeSocketChainList({ payload: { ticket } }) {
  const webSocketConnector = yield safeCall(WebSocketConnector.build);

  const identifier = {
    channel: SOCKET_CHAIN_CHANNEL,
    ticket_id: ticket.id,
  };

  webSocketConnector.subscribeChannel(identifier);

  yield safeCall(watchSocketChainListMessage, webSocketConnector, identifier);
}

export default function* () {
  yield all([
    yield takeLeading(FETCH_CHAIN, safeApply(fetchChain)),
    yield takeLatest(
      FETCH_CHAIN_SUCCESS,
      safeApply(setMailTemplatesGroupsInChain)
    ),
    yield takeLatest(
      FETCH_CHAIN_SUCCESS,
      safeApply(subscribeSocketActiveChain)
    ),
    yield takeLatest(FETCH_CHAIN_SUCCESS, safeApply(fetchCopilotSuggestions)),
    yield takeLatest(FETCH_CHAIN, safeApply(unsubscribeSocketActiveChain)),
    yield takeLatest(SELECT_TICKET, safeApply(unsubscribeSocketActiveChain)),
    yield takeLatest(SELECT_TICKET, safeApply(unsubscribeSocketChainList)),
    yield takeLatest(
      CREATE_CHAIN_REQUEST,
      safeApply(setMailTemplatesGroupsInChain)
    ),
    yield takeLatest(
      CREATE_CHAIN_REQUEST,
      safeApply(unsubscribeSocketActiveChain)
    ),
    yield takeLatest(SET_TICKET, safeApply(subscribeSocketChainList)),
    yield takeEvery(CREATE_TICKET_SUCCESS, safeApply(subscribeSocketChainList)),
    yield takeLatest(CREATE_CHAIN, safeApply(createChain)),
    yield takeEvery(SEND_MESSAGE, safeApply(sendMessage)),
    yield takeEvery(FORWARD_MESSAGE, safeApply(forwardMessage)),
    yield takeEvery(FORWARD_CHAIN, safeApply(forwardChain)),
    yield takeLatest(
      [SET_TICKET, CREATE_TICKET_SUCCESS],
      safeApply(fetchChains)
    ),
    yield takeLatest(LOAD_MESSAGES_PAGE, safeApply(fetchMessagesPage)),
    yield takeLatest(UPDATE_ACTIVE_CHAIN, safeApply(updateActiveChain)),
    yield takeLatest(SET_MAIL_TEMPLATES, safeApply(setMailTemplatesInChain)),
    yield takeLatest(APPLY_MAIL_TEMPLATE, safeApply(logApplyMailTemplate)),
    yield takeLatest(SET_MAIL_TEMPLATE, safeApply(setMailTemplateInChain)),
    yield takeLatest(
      SEARCH_MAIL_TEMPLATES,
      safeApply(searchMailTemplatesInChain)
    ),
    yield takeLatest(
      SELECT_FOUND_MAIL_TEMPLATE,
      safeApply(selectFoundMailTemplateInChain)
    ),
    yield takeLatest(
      LOAD_HISTORY_MESSAGES_IN_CHAIN,
      safeApply(loadHistoryMessages)
    ),
  ]);
}
